import PropTypes from 'prop-types';
import React, { useRef } from 'react';
import { Link } from 'react-router-dom';

import { Button, OTPInput } from '../../../components';

const OTP_LENGTH = 6;

/**
 * Step 4 — 2FA confirmation. The backend mandates 2FA for refunds/accepts,
 * so a merchant without 2FA enabled is prompted to enable it instead of
 * being shown the OTP boxes.
 */
const TwoFactorStep = ({
  twofaEnabled,
  otp,
  onOtpChange,
  onContinue,
  onBack,
  isSubmitting,
  error
}) => {
  const otpRef = useRef(null);

  const pasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      otpRef.current?.fill(text);
    } catch {
      // Clipboard read can be blocked; the manual entry path still works.
    }
  };

  if (!twofaEnabled) {
    return (
      <div className="resolve resolve--twofa">
        <p className="resolve__subtitle" data-testid="twofa-disabled-message">
          You need to enable two-factor authentication before you can confirm a refund.
        </p>
        <div className="resolve__actions">
          <Button variant="outline" type="button" onClick={onBack} data-testid="twofa-back-btn">
            Back
          </Button>
          <Link className="btn" to="/settings/security" data-testid="enable-2fa-link">
            Enable 2FA
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="resolve resolve--twofa">
      <p className="resolve__subtitle" data-testid="twofa-prompt">
        Enter the 6-digit code from your authenticator app to confirm this refund.
      </p>

      <OTPInput ref={otpRef} length={OTP_LENGTH} onChange={onOtpChange} error={error} />
      <button type="button" className="resolve__paste" onClick={pasteFromClipboard}>
        Paste code
      </button>

      <div className="resolve__actions">
        <Button variant="outline" type="button" onClick={onBack} data-testid="twofa-back-btn">
          Back
        </Button>
        <Button
          type="button"
          onClick={onContinue}
          isLoading={isSubmitting}
          disabled={(otp || '').length !== OTP_LENGTH}
          data-testid="twofa-continue-btn"
        >
          Continue
        </Button>
      </div>
    </div>
  );
};

TwoFactorStep.propTypes = {
  twofaEnabled: PropTypes.bool,
  otp: PropTypes.string,
  onOtpChange: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
  error: PropTypes.string
};

export { TwoFactorStep };
